/**
 * "Send back": return a record you hold to the person who had it before, with
 * a reason, signed by a fresh authenticator code. One dialog for all four
 * flows (PA, UMED, Finance, Manifest); the caller names the preview endpoint.
 *
 * Hand-built rather than the kit's Radix Dialog, which this project's Jest
 * cannot load (see pages/admin/bd/bdUi.tsx Drawer). It keeps what a modal owes:
 * a labelled dialog, focus moved in on open and back on close, Tab held inside,
 * Escape and the backdrop close it, and the page behind does not scroll.
 *
 * What it does NOT do: decide anything. The preview says whether the act is
 * allowed and who receives it; the server checks again on the act, and the
 * recipient is echoed back so a record that changed hands since the preview
 * is refused (TARGET_CHANGED) instead of silently re-routed.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import {
  SendBackBody, SendBackPreview, SendBackResult, SendBackTarget,
  newIdempotencyKey, refusalCopy, sendBackApi,
} from './sendBackApi';
import ConfirmWithMicrosoft from '../Auth/ConfirmWithMicrosoft';
import { STEP_UP_SSO_REQUIRED } from '../../services/ssoApi';
import './sendBack.css';

export const REASON_MIN = 8;
export const REASON_MAX = 2000;

const ZONE = 'America/New_York';
const signedWords = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: ZONE, timeZoneName: 'short' });
};

const NOTICE_WORDS: Record<string, string> = {
  PENDING: 'An email to them is being sent.',
  SENT: 'They were emailed.',
  SKIPPED: 'No email was sent.',
  FAILED: 'The email could not be sent; the send-back itself stands.',
};
const noticeWords = (state?: string): string => (state ? NOTICE_WORDS[state] || `Email: ${state.toLowerCase()}.` : '');

/** Who it goes to, in the words the business uses. */
export function Recipient({ to }: { to: SendBackTarget }) {
  return (
    <div className="sb-who">
      {to.originating
        ? <p>Goes to <strong>{to.name}</strong> (originally sent by <strong>{to.originating.name}</strong>)</p>
        : <p>Goes back to <strong>{to.name}</strong>{to.confidence === 'SOURCE_REPORTED' ? <span className="sb-quiet"> · from the Monday record</span> : null}</p>}
      {to.source === 'SCHEDULING_BUILDING_LATEST' && <p className="sb-quiet">The building&apos;s current provider, not confirmed for this visit.</p>}
      {to.source === 'SCHEDULING_REVIEW_ROW' && <p className="sb-quiet">The provider on this visit&apos;s schedule.</p>}
      {to.emailOnly && <p className="sb-quiet">{to.name} has no eMerge login; they&apos;ll get an email.</p>}
    </div>
  );
}

/** The ids the preview named, echoed so the server can tell if the record moved on. */
function echo(to: SendBackTarget): Pick<SendBackBody, 'to_user_id' | 'to_employee_id' | 'to_monday_user_id'> {
  const out: Pick<SendBackBody, 'to_user_id' | 'to_employee_id' | 'to_monday_user_id'> = {};
  if (to.userId) out.to_user_id = to.userId;
  if (to.employeeId) out.to_employee_id = to.employeeId;
  if (to.mondayUserId !== null && to.mondayUserId !== undefined && to.mondayUserId !== '') out.to_monday_user_id = to.mondayUserId;
  return out;
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface SendBackDialogProps {
  open: boolean;
  onClose: () => void;
  /** The GET/POST endpoint for this record, from `sendBackPath`. */
  path: string;
  /** What is being sent back, for the title ("Send back Ada Fixture"). */
  recordLabel?: string;
  /** Called once when the server accepts (201) or replays (200) the act. */
  onSent?: (result: SendBackResult) => void;
  /** Text the reason starts with (editable). Used where the screen already knows why, e.g. a blocker only the PA can clear. */
  initialReason?: string;
}

export default function SendBackDialog(props: SendBackDialogProps) {
  if (!props.open) return null;
  // Mounted per opening, so the idempotency key and every field start fresh.
  return <Panel {...props} />;
}

function Panel({ onClose, path, recordLabel, onSent, initialReason = '' }: SendBackDialogProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const titleId = useId();
  const reasonId = useId();
  const reasonErrId = useId();
  const codeId = useId();
  const codeErrId = useId();

  const [preview, setPreview] = useState<SendBackPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The recipient being confirmed. It starts as the preview's and is replaced when the server says it changed.
  const [target, setTarget] = useState<SendBackTarget | null>(null);
  const [changedNote, setChangedNote] = useState<string | null>(null);
  const [reason, setReason] = useState(initialReason);
  const [code, setCode] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<{ text: string; settingsPath?: string; reopen?: boolean; microsoft?: boolean } | null>(null);
  const [reload, setReload] = useState(0);
  const [sent, setSent] = useState<SendBackResult | null>(null);
  const [key, setKey] = useState(newIdempotencyKey);
  const busyRef = useRef(false);
  busyRef.current = busy;

  // Focus in, Escape out, Tab held inside, page behind still. Depends on mount only.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      // Escape is this dialog's: marked handled, so a panel behind it (the PA case panel closes on an
      // unhandled Escape) stays open.
      if (e.key === 'Escape') { e.preventDefault(); if (!busyRef.current) closeRef.current(); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) { e.preventDefault(); panelRef.current.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const at = document.activeElement;
      if (e.shiftKey && (at === first || at === panelRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      before?.focus?.();
    };
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      const r = await sendBackApi.preview(path);
      if (!live) return;
      if (r.success && r.data) { setPreview(r.data); setTarget(r.data.to); } else setLoadError(refusalCopy(r));
      setLoading(false);
    })();
    return () => { live = false; };
  }, [path, reload]);

  // STALE: the record's send-back count moved. Re-read the preview (new version, recipient) and start the form clean.
  const reopen = () => {
    setPreview(null); setTarget(null); setLoadError(null); setProblem(null); setChangedNote(null);
    setCode(''); setTried(false); setLoading(true); setKey(newIdempotencyKey()); setReload((n) => n + 1);
  };
  const reasonText = reason.trim();
  const reasonError = reasonText.length < REASON_MIN ? `Say why, in at least ${REASON_MIN} characters. The reason goes to ${target?.name || 'them'} and stays on the record.` : null;
  // A Microsoft-linked sender with no authenticator confirms with a fresh Microsoft sign-in instead of a code (preview.ssoStepUp).
  const microsoftMode = Boolean(preview && !preview.hasAuthenticator && preview.ssoStepUp);
  const codeError = microsoftMode || /^\d{6}$/.test(code) ? null : 'Enter the 6-digit code from your authenticator app.';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!preview || !target || busy || sent) return;
    setTried(true);
    if (reasonError || codeError) return;
    setBusy(true); setProblem(null);
    const body: SendBackBody = { reason: reasonText, totp_code: microsoftMode ? '' : code, ...echo(target) };
    if (preview.version !== null && preview.version !== undefined) body.version = preview.version;
    const r = await sendBackApi.send(path, body, key);
    setBusy(false);
    if (r.success && r.data) { setSent(r.data); onSent?.(r.data); return; }

    setCode('');
    if (r.code === 'TARGET_CHANGED' && r.detail?.to) {
      // The record changed hands since the preview: show who it goes to now and ask again.
      // Nothing was recorded, and this is a different act, so it gets its own key.
      setTarget(r.detail.to as SendBackTarget);
      setKey(newIdempotencyKey());
      setChangedNote(`This record has changed hands since you opened this. It now goes to ${r.detail.to.name}. Confirm again with a new code.`);
      setTried(false);
      return;
    }
    if (r.code === 'STEP_UP_UNAVAILABLE') {
      setProblem({ text: 'Set up an authenticator first. Sending work back is signed with a code from your authenticator app.', settingsPath: r.detail?.settingsPath || '/portal/settings' });
      return;
    }
    if (r.code === STEP_UP_SSO_REQUIRED) {
      // A Microsoft-signed-in sender confirms with Microsoft instead of typing a code, then sends again.
      setProblem({ text: r.message || 'Confirm with Microsoft first, then send this back again.', microsoft: true });
      return;
    }
    if (r.code === 'STALE') {
      setProblem({ text: 'Someone sent this back a moment ago; reopen to see the current state.', reopen: true });
      return;
    }
    setProblem({ text: refusalCopy(r) });
  };

  let body: React.ReactNode;
  if (loading) body = <p role="status" className="sb-quiet">Checking who this goes back to…</p>;
  else if (loadError) body = <p role="alert" className="sb-alert">{loadError}</p>;
  else if (preview && !preview.can) {
    body = <p role="alert" className="sb-alert">{preview.refusal ? refusalCopy({ code: preview.refusal.code, message: preview.refusal.message, status: 409 }) : 'This cannot be sent back right now.'}</p>;
  } else if (sent) {
    body = (
      <div role="status" className="sb-done">
        <p><strong>Sent back to {sent.to.name}.</strong> Signed {signedWords(sent.signedAt)}.</p>
        {sent.to.emailOnly || target?.emailOnly ? <p className="sb-quiet">{sent.to.name} has no eMerge login, so they get an email only.</p> : null}
        {noticeWords(sent.notice?.state) && <p className="sb-quiet">{noticeWords(sent.notice?.state)}</p>}
        {sent.replayed && <p className="sb-quiet">This send-back had already been recorded; nothing new was sent.</p>}
      </div>
    );
  } else if (preview && target) {
    const noAuthenticator = !preview.hasAuthenticator && !microsoftMode;
    body = (
      <form onSubmit={submit} noValidate aria-label="Send back">
        {changedNote && <p role="alert" className="sb-alert">{changedNote}</p>}
        <Recipient to={target} />
        <p className="sb-step">Currently at: <strong>{preview.fromStep || 'step not recorded'}</strong></p>

        <label htmlFor={reasonId} className="sb-label">Why you are sending it back</label>
        <textarea
          id={reasonId} value={reason} rows={4} maxLength={REASON_MAX} disabled={busy} required aria-required
          onChange={(e) => setReason(e.target.value)}
          aria-invalid={tried && reasonError ? true : undefined} aria-describedby={tried && reasonError ? reasonErrId : undefined}
        />
        {tried && reasonError && <p id={reasonErrId} role="alert" className="sb-alert">{reasonError}</p>}

        {microsoftMode ? (
          <p className="sb-quiet">
            Confirm it is you with Microsoft, then come back and press Send back within 10 minutes.{' '}
            <ConfirmWithMicrosoft purpose="SEND_BACK" className="sb-ghost" />
          </p>
        ) : noAuthenticator ? (
          <p role="alert" className="sb-alert">
            Set up an authenticator first. Sending work back is signed with a code from your authenticator app.{' '}
            <a href="/portal/settings">Open settings</a>
          </p>
        ) : (
          <>
            <label htmlFor={codeId} className="sb-label">Authenticator code</label>
            <input
              id={codeId} className="sb-code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} disabled={busy}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              aria-required aria-invalid={tried && codeError ? true : undefined} aria-describedby={tried && codeError ? codeErrId : undefined}
            />
            {tried && codeError && <p id={codeErrId} role="alert" className="sb-alert">{codeError}</p>}
            <p className="sb-quiet">Your authenticator code signs this send-back with your name and the time.</p>
          </>
        )}

        {problem && (
          <p role="alert" className="sb-alert">
            {problem.text}{problem.settingsPath ? <>{' '}<a href={problem.settingsPath}>Open settings</a></> : null}
            {problem.reopen ? <>{' '}<button type="button" className="sb-ghost" onClick={reopen}>Reopen</button></> : null}
            {problem.microsoft ? <>{' '}<ConfirmWithMicrosoft purpose="SEND_BACK" className="sb-ghost" /></> : null}
          </p>
        )}

        <div className="sb-acts">
          <button type="submit" className="sb-go" disabled={busy || noAuthenticator}>{busy ? 'Sending back…' : 'Send back'}</button>
          <button type="button" className="sb-ghost" onClick={() => closeRef.current()} disabled={busy}>Cancel</button>
        </div>
      </form>
    );
  } else body = null;

  const showClose = loading || loadError || (preview && !preview.can) || sent;

  return (
    <div className="sb-root">
      <div className="sb-backdrop" aria-hidden onClick={() => { if (!busy) closeRef.current(); }} />
      <div ref={panelRef} className="sb-panel" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <h2 id={titleId} className="sb-title">{recordLabel ? `Send back ${recordLabel}` : 'Send back'}</h2>
        {body}
        {showClose && (
          <div className="sb-acts"><button type="button" className="sb-ghost" onClick={() => closeRef.current()}>Close</button></div>
        )}
      </div>
    </div>
  );
}

/**
 * The control a workspace places: a button in the workspace's own style that
 * opens the dialog. The caller decides whether to render it at all (the scope
 * flag); this does not second-guess that, and the dialog's preview and the
 * server decide again.
 */
export function SendBackAction({ path, label = 'Send back', recordLabel, className = 'sb-trigger', initialReason, onSent, onClosed }: {
  path: string; label?: string; recordLabel?: string; className?: string;
  /** The reason field starts with this text; the sender may edit it. */
  initialReason?: string;
  /** When the server accepts the act. The dialog stays open to say so. */
  onSent?: (r: SendBackResult) => void;
  /**
   * When the dialog closes, with the result if one was recorded. A workspace
   * that refreshes here (rather than in `onSent`) keeps the confirmation on
   * screen: after a send-back the record is no longer the sender's, and
   * re-reading it can remove the very screen the dialog sits on.
   */
  onClosed?: (r: SendBackResult | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const done = useRef<SendBackResult | null>(null);
  return (
    <>
      <button type="button" className={className} aria-haspopup="dialog" onClick={() => { done.current = null; setOpen(true); }}>{label}</button>
      <SendBackDialog
        open={open} path={path} recordLabel={recordLabel} initialReason={initialReason}
        onSent={(r) => { done.current = r; onSent?.(r); }}
        onClose={() => { setOpen(false); onClosed?.(done.current); }}
      />
    </>
  );
}
